import React from 'react';
import { z } from 'zod';
import type { Candidate, Certification } from '@axiom/validation';
import { CertificationSchema } from '@axiom/validation';
import {
  Button,
  Form,
  Modal,
  ModalHeader,
  ModalSection,
  ModalFooter,
  Gutter,
  Input,
  Dropdown,
  Grid,
  GridRow,
  GridColumn,
  DateUtil,
} from '@axiom/ui';
import { Certification as CertificationConst } from '@axiom/const';
import { CertificationModel } from '@axiom/ui-models';

import { CandidateApi } from '../../api/candidate-api';
import { CandidateCertificationsApi } from '../../api/candidate-certifications-api';
import { CandidateProfileIssuesApi } from '../../api/candidate-profile-issues-api';

import { AccomplishmentsBannerPrompt } from './AccomplishmentsBannerPrompt';

const {
  QUALIFICATION_AWARDS: { QUALIFICATION, YEAR_OF_CALL, STATE_EXAM },
  FORM_TYPES: { QUALIFICATIONS, CERTIFICATIONS_AND_AWARDS },
} = CertificationConst;

const formSchema = CertificationSchema.pick({
  award: true,
  institution: true,
  stateExamScore: true,
  year: true,
}).extend({
  type: z.enum([CERTIFICATIONS_AND_AWARDS, QUALIFICATIONS]),
});

const accomplishmentsYears = DateUtil.getYearOptionsByRange(
  new Date().getFullYear(),
  1950
);

const qualificationYears = DateUtil.getYearOptionsByRange(
  new Date().getFullYear() + 10,
  1950
);

const qualificationOptions = [QUALIFICATION, STATE_EXAM, YEAR_OF_CALL].map(
  value => ({ value })
);

export type AccomplishmentsItemEditProps = {
  name?: string;
  candidateId: Candidate['id'];
  certification?: Partial<Certification>;
  onClose?: () => void;
  showBanner?: boolean;
};

export const AccomplishmentsItemEdit = ({
  name = 'ACCOMPLISHMENTS_ITEM_EDIT_FORM',
  candidateId,
  certification,
  onClose = () => {},
  showBanner = false,
}: AccomplishmentsItemEditProps) => {
  const certModel = new CertificationModel(certification);
  const getInitialType = () => {
    return certModel.isQualification()
      ? QUALIFICATIONS
      : CERTIFICATIONS_AND_AWARDS;
  };

  return (
    <Form
      name={name}
      schema={formSchema}
      initialValues={{
        ...certification,
        stateExamScore: certification?.stateExamScore ?? null,
        type: certification?.id ? getInitialType() : undefined,
      }}
      onSubmit={async formData => {
        const submitData = { ...certification, ...formData };

        if (!submitData.stateExamScore) {
          delete submitData.stateExamScore;
        }

        await (certification?.id
          ? CandidateCertificationsApi.updateCertification(
              certification.id,
              formSchema.partial().parse(submitData)
            )
          : CandidateCertificationsApi.createCertification(
              candidateId,
              CertificationSchema.partial().parse(submitData) as Certification
            ));
        await CandidateApi.refreshCandidate(candidateId);
        await CandidateProfileIssuesApi.refreshCandidateProfileIssues(
          candidateId
        );
        onClose();
      }}
    >
      {({ fireSubmit, values }) => {
        return (
          <Modal name={`${name}_MODAL`}>
            <ModalHeader name={`${name}_MODAL_HEADER`} onClose={onClose}>
              Edit Accomplishments
            </ModalHeader>
            <ModalSection disableScrolling>
              {!!showBanner && <AccomplishmentsBannerPrompt />}
              <Gutter bottom="16px" />
              <Dropdown
                displayKey="value"
                name="type"
                options={[CERTIFICATIONS_AND_AWARDS, QUALIFICATIONS].map(
                  value => ({ value })
                )}
                valueKey="value"
                label="Accomplishment Type"
                disabled={!!certification?.id}
              />
              <Gutter bottom="16px" />
              {values.type === CERTIFICATIONS_AND_AWARDS && (
                <Grid name="CERTIFICATIONS_AND_AWARDS_FIELDS">
                  <GridRow gutterBottom="16px">
                    <GridColumn>
                      <Input name="award" label="Certification or Award Name" />
                    </GridColumn>
                  </GridRow>
                  <GridRow>
                    <GridColumn largeScreenWidth={6} smallScreenWidth={12}>
                      <Input name="institution" label="Awarding Organization" />
                      <Gutter bottom="16px" only="smallScreen" />
                    </GridColumn>
                    <GridColumn largeScreenWidth={6} smallScreenWidth={12}>
                      <Dropdown
                        displayKey="label"
                        name="year"
                        options={accomplishmentsYears}
                        valueKey="value"
                        label="Year Received"
                      />
                    </GridColumn>
                  </GridRow>
                </Grid>
              )}
              {values.type === QUALIFICATIONS && (
                <Grid name="QUALIFICATION_FIELDS">
                  <GridRow gutterBottom="16px">
                    <GridColumn>
                      <Input name="institution" label="Institution" />
                    </GridColumn>
                  </GridRow>
                  <GridRow>
                    <GridColumn largeScreenWidth={6} smallScreenWidth={12}>
                      <Dropdown
                        displayKey="value"
                        name="award"
                        options={qualificationOptions}
                        valueKey="value"
                        label="Qualification Type"
                      />
                      <Gutter bottom="16px" only="smallScreen" />
                    </GridColumn>
                    <GridColumn largeScreenWidth={6} smallScreenWidth={12}>
                      <Dropdown
                        displayKey="label"
                        name="year"
                        options={qualificationYears}
                        valueKey="value"
                        label="Year"
                      />
                    </GridColumn>
                  </GridRow>
                  {values.award === STATE_EXAM && (
                    <GridRow gutterTop="16px">
                      <GridColumn>
                        <Input
                          name="stateExamScore"
                          label="Score"
                          description="Saved scores are not visible in your profile or shared in client submissions."
                        />
                      </GridColumn>
                    </GridRow>
                  )}
                </Grid>
              )}
            </ModalSection>
            <ModalFooter>
              <Button variation="outline" onClick={onClose} name="CANCEL">
                Cancel
              </Button>
              <Button type="submit" name="SAVE" onClick={fireSubmit}>
                Save
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
